/**
 * StyleGuideChecker.js — Main orchestrator component
 *
 * This is the top-level React component that ties together:
 *   - Document preview (docx-preview rendering + highlight overlays)
 *   - Issue tracking (accept/dismiss/revert individual or bulk)
 *   - Undo/redo (snapshot-based, 50-entry stack limit)
 *   - Session persistence (save to backend, resume on reload)
 *   - Navigation guards (warn before leaving with unresolved issues)
 *
 * ═══════════════════════════════════════════════════════════
 *  DATA FLOW OVERVIEW
 * ═══════════════════════════════════════════════════════════
 *
 * 1. Parent calls loadFile(buffer) → unzips DOCX, parses XML, renders preview
 * 2. Parent calls loadIssues(session) → resolves Gemini issues to offsets
 *    (or replays accepted edits from a saved session)
 * 3. User interacts via popover/tracking panel → accept/dismiss/revert
 * 4. Each action: validates → pushes undo snapshot → mutates XML → updates
 *    issues/offsets → re-renders preview → rebuilds highlights
 *
 * ═══════════════════════════════════════════════════════════
 *  UNDO/REDO ARCHITECTURE
 * ═══════════════════════════════════════════════════════════
 *
 * Each undo entry is a full snapshot: { docXml, issues, paragraphs }
 *   - pushUndo() captures state AFTER validation but BEFORE mutation
 *   - undo() pops the last snapshot, pushes current state to redo
 *   - redo() does the reverse
 *   - clearUndoRedo() resets both stacks (on file/session load)
 *   - Max 50 entries to cap memory usage
 *
 * ═══════════════════════════════════════════════════════════
 *  REF SYNCHRONIZATION PATTERN
 * ═══════════════════════════════════════════════════════════
 *
 * React state (issues, docXml, paragraphs) is the source of truth.
 * Mutable refs (issuesRef, docXmlRef, paragraphsRef) mirror state
 * via useEffect so that undo snapshots always capture the latest
 * committed values without stale closure issues.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Grid } from '@mui/material';
import { renderAsync } from 'docx-preview';
import { useBlocker } from 'react-router-dom';
import { UnaddressedModal } from './UnaddressedModal.js';
import { IssuePopover } from './IssuePopover.js';
import { TrackingPanel } from './TrackingPanel.js';
import { PreviewPanel } from './PreviewPanel.js';
import {
  DOC_XML_PATH,
  unzipDocx,
  getTextFromZip,
  setTextInZip,
  zipToArrayBuffer,
  downloadZipAsDocx,
  parseDocXmlToParagraphs,
  injectParagraphIds,
  applyReplacementInParagraph,
  previewShort,
} from './utils/docx.js';
import { computeOffsets } from './utils/gemini.js';
import {
  updateIssuesAfterAccept,
  rebuildOpenAnchors,
  initializeIssueAnchors,
} from './utils/issue.js';
import {
  applyHighlightsToPreview,
  highlightSelected,
  applySelectedStyle,
  clearPreviewHighlights,
  buildAnchor,
} from './utils/highlight.js';
import { useInterceptWindowOpen } from '../../common/utils.js';
import { patchData } from '../../../DataAccessLayer/index.js';
import { esga } from '../../../DataAccessLayer/services.js';
import { toast } from 'react-toastify';
import { ESGA_AUTO_SAVE_INTERVAL } from '../../common/constants/esga.js';

/* ══════════════════════════════════════════════════════════════
 *  StyleGuideChecker
 * ══════════════════════════════════════════════════════════════ */
export const StyleGuideChecker = forwardRef((props, ref) => {
  /* ──────────────────────────────────────────────────────────
   *  Refs — mutable values that persist across renders
   * ────────────────────────────────────────────────────────── */

  /** DOM element where docx-preview renders the document. */
  const previewContainerRef = useRef(null);

  /** Map of issueId → highlight <span> element in the preview DOM. */
  const highlightMapRef = useRef(new Map());

  /** Popper.js instance for positioning the floating issue popover. */
  const popperRef = useRef(null);

  /** The mutable JSZip instance representing the current document.
   *  All accept/revert operations write directly into this zip. */
  const workingZipRef = useRef(null);

  /** Timer ID for debounced preview rendering. */
  const renderTimerRef = useRef(null);

  /**
   * Canonical issues with original-doc offsets and stable IDs.
   * Set once on first analysis, never mutated afterwards. Used for:
   *   - Saving to backend (doDownload)
   *   - Replaying accepted edits on resume (loadIssues)
   *   - Resetting to clean state (undoAll)
   */
  const canonicalIssuesRef = useRef(null);

  /* ──────────────────────────────────────────────────────────
   *  State — React-managed values that trigger re-renders
   * ────────────────────────────────────────────────────────── */

  /** True while an async operation (accept, revert, render) is in progress. */
  const [trackingMinimized, setTrackingMinimized] = useState(false);

  const [isBusy, setIsBusy] = useState(false);

  /** The loaded DOCX file buffer (null before a file is loaded). */
  const [loadedFile, setLoadedFile] = useState(null);

  /** Original unmodified file buffer (for potential reset/re-download). */
  const [originalFileBuffer, setOriginalFileBuffer] = useState(null);

  /** Current document XML string (word/document.xml content). */
  const [currentDocXml, setCurrentDocXml] = useState('');

  /** Current paragraph model parsed from the XML. */
  const [currentParagraphs, setCurrentParagraphs] = useState([]);
  /** Mutable ref mirror of currentParagraphs for undo snapshots. */
  const paragraphsRef = useRef([]);

  /** All style-guide issues with their current status and offsets. */
  const [issues, setIssues] = useState([]);

  /** ID of the currently selected/highlighted issue (or null). */
  const [selectedIssueId, setSelectedIssueId] = useState(null);

  /** DOM element the popover is anchored to (a highlight span). */
  const [popoverAnchorElement, setPopoverAnchorElement] = useState(null);

  /** Whether the issue popover is visible. */
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  /** Counter that increments on each preview render, triggering highlight effects. */
  const [previewRenderKey, setPreviewRenderKey] = useState(0);

  /** Which tab is active in the tracking panel: 'suggestions' or 'changes'. */
  const [activeTab, setActiveTab] = useState('suggestions');

  /* ── Unaddressed-issues modal state ── */
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [pendingExternalUrl, setPendingExternalUrl] = useState(null);
  const [isPendingDownload, setIsPendingDownload] = useState(false);

  /* ──────────────────────────────────────────────────────────
   *  Derived values — computed from state, memoized
   * ────────────────────────────────────────────────────────── */

  /** The full issue object for the currently selected issue (or null). */
  const selectedIssue = useMemo(
    () => issues.find((issue) => issue.id === selectedIssueId) || null,
    [issues, selectedIssueId],
  );

  /** All issues with status='open' — shown in the Suggestions tab. */
  const openIssues = useMemo(() => issues.filter((issue) => issue.status === 'open'), [issues]);

  /**
   * Display number for each open issue (1-indexed).
   * Used by the tracking panel list and highlight badges.
   */
  const issueNumberById = useMemo(() => {
    const numberMap = new Map();
    openIssues.forEach((issue, index) => numberMap.set(issue.id, index + 1));
    return numberMap;
  }, [openIssues]);

  /** All issues with status='accepted' — shown in the Changes tab. */
  const acceptedIssues = useMemo(
    () => issues.filter((issue) => issue.status === 'accepted'),
    [issues],
  );

  /** True when there are unresolved suggestions AND a file is loaded.
   *  Used by navigation guards and download warnings. */
  const hasUnresolvedIssues = openIssues.length > 0 && loadedFile !== null;

  /* ══════════════════════════════════════════════════════════════
   *  Preview rendering
   *
   *  Converts the current zip into a visible HTML preview using
   *  docx-preview, while preserving the user's scroll position.
   * ══════════════════════════════════════════════════════════════ */

  const renderDocxFromZip = useCallback(async (zip) => {
    if (!previewContainerRef.current) return;
    const container = previewContainerRef.current;

    // ── Save scroll positions of all scrollable ancestors ──
    // So the preview doesn't jump to the top after re-rendering.
    const savedScrollPositions = [];
    let scrollableElement = container;
    while (scrollableElement) {
      if (scrollableElement.scrollTop > 0) {
        savedScrollPositions.push({
          element: scrollableElement,
          top: scrollableElement.scrollTop,
          left: scrollableElement.scrollLeft,
        });
      }
      scrollableElement = scrollableElement.parentElement;
    }
    if (!savedScrollPositions.find((saved) => saved.element === container)) {
      savedScrollPositions.push({
        element: container,
        top: container.scrollTop,
        left: container.scrollLeft,
      });
    }

    try {
      // Read current XML from the working zip.
      const documentXml = await getTextFromZip(zip, DOC_XML_PATH);

      // Inject paragraph IDs so highlight.js can map paragraphs to DOM elements.
      const xmlWithParagraphIds = injectParagraphIds(documentXml);

      // Clone the zip for rendering (don't mutate the working zip with injected IDs).
      const clonedBuffer = await zipToArrayBuffer(zip);
      const renderZip = await unzipDocx(clonedBuffer);
      setTextInZip(renderZip, DOC_XML_PATH, xmlWithParagraphIds);
      const renderBuffer = await zipToArrayBuffer(renderZip);

      // Clear old preview and render the new document.
      container.innerHTML = '';
      await renderAsync(renderBuffer, container, null, {
        inWrapper: true,
        breakPages: true,
        ignoreFonts: false,
      });
    } catch {
      // Fallback: render without paragraph ID injection.
      const fallbackBuffer = await zipToArrayBuffer(zip);
      container.innerHTML = '';
      await renderAsync(fallbackBuffer, container, null, {
        inWrapper: true,
        breakPages: true,
        ignoreFonts: false,
      });
    }

    // ── Restore scroll positions after render ──
    requestAnimationFrame(() => {
      for (const saved of savedScrollPositions) {
        saved.element.scrollTop = saved.top;
        saved.element.scrollLeft = saved.left;
      }
    });

    // Bump key so highlight effects know a new preview is ready.
    setPreviewRenderKey((key) => key + 1);
  }, []);

  /* ══════════════════════════════════════════════════════════════
   *  Undo / Redo system
   *
   *  Snapshot-based: each entry stores { docXml, issues, paragraphs }.
   *  pushUndo() captures state BEFORE mutation (called AFTER validation).
   *  undo()/redo() swap between stacks and restore via restoreSnapshot().
   * ══════════════════════════════════════════════════════════════ */

  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  /** Counter that triggers re-render when stacks change (for button enable/disable). */
  const [undoRedoVersion, setUndoRedoVersion] = useState(0);

  /** Mutable ref mirror of issues state (for undo snapshots). */
  const issuesRef = useRef([]);
  /** Mutable ref mirror of currentDocXml state (for undo snapshots). */
  const docXmlRef = useRef('');

  // Keep refs in sync with state. Effects run after render, so refs always
  // reflect the last committed state when the next event handler fires.
  useEffect(() => {
    issuesRef.current = issues;
  }, [issues]);
  useEffect(() => {
    docXmlRef.current = currentDocXml;
  }, [currentDocXml]);

  /** True when there's at least one snapshot to undo. */
  const canUndo = undoStackRef.current.length > 0;
  /** True when there's at least one snapshot to redo. */
  const canRedo = redoStackRef.current.length > 0;

  /** Maximum number of undo snapshots to keep (caps memory usage). */
  const MAX_UNDO_HISTORY = 50;

  /**
   * Capture the current committed state onto the undo stack.
   * Called AFTER validation succeeds but BEFORE state mutation,
   * so the snapshot represents the "before" state we can revert to.
   */
  const pushUndoSnapshot = useCallback(() => {
    undoStackRef.current.push({
      docXml: docXmlRef.current,
      issues: issuesRef.current,
      paragraphs: paragraphsRef.current,
    });
    // Drop oldest entry if over the limit.
    if (undoStackRef.current.length > MAX_UNDO_HISTORY) undoStackRef.current.shift();
    // Any new action invalidates the redo history.
    redoStackRef.current = [];
    // Trigger re-render so undo/redo buttons update.
    setUndoRedoVersion((version) => version + 1);
  }, []);

  /**
   * Restore a snapshot into the working zip + React state, then re-render.
   * Used by both undo() and redo().
   */
  const restoreSnapshot = useCallback(
    async (snapshot) => {
      const zip = workingZipRef.current;
      if (!zip) return;
      setIsBusy(true);
      try {
        // Write snapshot XML back into the working zip.
        setTextInZip(zip, DOC_XML_PATH, snapshot.docXml);
        // Update all React state to match the snapshot.
        setCurrentDocXml(snapshot.docXml);
        setCurrentParagraphs(snapshot.paragraphs);
        paragraphsRef.current = snapshot.paragraphs;
        setIssues(snapshot.issues);
        // Close popover (highlight spans are about to be destroyed by re-render).
        setIsPopoverOpen(false);
        setSelectedIssueId(null);
        setPopoverAnchorElement(null);
        // Re-render preview to reflect the restored document.
        await renderDocxFromZip(zip);
      } finally {
        setIsBusy(false);
        setUndoRedoVersion((version) => version + 1);
      }
    },
    [renderDocxFromZip],
  );

  /** Undo: pop the most recent snapshot and restore it. Push current state to redo. */
  const undo = useCallback(async () => {
    if (!undoStackRef.current.length) return;
    // Save current state to redo before stepping back.
    redoStackRef.current.push({
      docXml: docXmlRef.current,
      issues: issuesRef.current,
      paragraphs: paragraphsRef.current,
    });
    const previousSnapshot = undoStackRef.current.pop();
    await restoreSnapshot(previousSnapshot);
  }, [restoreSnapshot]);

  /** Redo: pop the most recently undone snapshot and restore it. Push current state to undo. */
  const redo = useCallback(async () => {
    if (!redoStackRef.current.length) return;
    // Save current state to undo before stepping forward.
    undoStackRef.current.push({
      docXml: docXmlRef.current,
      issues: issuesRef.current,
      paragraphs: paragraphsRef.current,
    });
    const redoSnapshot = redoStackRef.current.pop();
    await restoreSnapshot(redoSnapshot);
  }, [restoreSnapshot]);

  /** Clear both stacks (used when loading a new file or session). */
  const clearUndoRedoHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setUndoRedoVersion((version) => version + 1);
  }, []);

  /* ══════════════════════════════════════════════════════════════
   *  Navigation / leave guards
   *
   *  Three layers prevent the user from accidentally losing work:
   *    1. window.open interceptor (sidebar links, external URLs)
   *    2. Browser beforeunload + visibilitychange
   *    3. React Router blocker
   * ══════════════════════════════════════════════════════════════ */

  // Layer 1: Intercept window.open calls (e.g., sidebar navigation links).
  useInterceptWindowOpen({
    isDirty: hasUnresolvedIssues,
    setOpenUnsavedChangesModal: setIsWarningModalOpen,
    setPendingExternalUrl,
    pendingExternalUrl,
  });

  // Layer 2: Native browser "unsaved changes" prompt + tab-switch warning.
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!hasUnresolvedIssues) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && hasUnresolvedIssues) {
        setIsWarningModalOpen(true);
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [hasUnresolvedIssues]);

  // Layer 3: React Router navigation blocker.
  const routeBlocker = useBlocker((tx) => {
    const differntPath = tx.currentLocation.pathname !== tx.nextLocation.pathname;
    if (hasUnresolvedIssues && differntPath) {
      setIsWarningModalOpen(true);
      return true; // Block the route transition.
    }
    return false; // Allow navigation.
  });

  /* ── Warning modal handlers ── */

  /** "Stay" button: dismiss the modal and cancel the pending action. */
  const handleStayOnPage = useCallback(() => {
    setPendingExternalUrl(null);
    setIsPendingDownload(false);
    setIsWarningModalOpen(false);
    if (routeBlocker.location) routeBlocker.reset();
  }, [routeBlocker]);

  /** Mutable ref so the modal's "Leave" handler can call the latest doDownload. */
  const doDownloadRef = useRef(null);

  /** "Leave" button: proceed with the pending action (download, route, or link). */
  const handleLeavePage = useCallback(async () => {
    setIsWarningModalOpen(false);
    if (isPendingDownload) {
      setIsPendingDownload(false);
      await doDownloadRef.current?.();
      return;
    }
    if (routeBlocker.location) {
      routeBlocker.proceed();
      return;
    }
    if (pendingExternalUrl) {
      const url = pendingExternalUrl;
      setPendingExternalUrl(null);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    }
  }, [routeBlocker, isPendingDownload, pendingExternalUrl]);

  /* ── Debounced render ──
   * Throttles expensive preview re-renders when multiple edits happen
   * in quick succession (e.g., rapid accept clicks).
   */
  const debouncedRender = useCallback(
    (zip) => {
      if (renderTimerRef.current) clearTimeout(renderTimerRef.current);
      renderTimerRef.current = setTimeout(() => renderDocxFromZip(zip), 120);
    },
    [renderDocxFromZip],
  );

  /* ══════════════════════════════════════════════════════════════
   *  loadFile — Initialize editor from a raw DOCX buffer
   *
   *  Called by the parent component after the user uploads a file.
   *  Unzips the DOCX, parses XML into a paragraph model, and
   *  renders the initial preview. Does NOT load issues yet —
   *  that happens in loadIssues() after Gemini analysis completes.
   * ══════════════════════════════════════════════════════════════ */

  /** Flag to defer rendering if the preview container isn't mounted yet. */
  const hasPendingRenderRef = useRef(false);

  const loadFile = useCallback(
    async (fileBuffer) => {
      setIsBusy(true);
      clearUndoRedoHistory();

      try {
        // Unzip the uploaded DOCX into a mutable in-memory zip.
        const zip = await unzipDocx(fileBuffer);
        const documentXml = await getTextFromZip(zip, DOC_XML_PATH);
        const { paragraphs: parsedParagraphs } = parseDocXmlToParagraphs(documentXml);

        // Store the file and zip for later operations.
        setLoadedFile(fileBuffer);
        setOriginalFileBuffer(fileBuffer);
        workingZipRef.current = zip;
        setCurrentDocXml(documentXml);
        setCurrentParagraphs(parsedParagraphs);
        paragraphsRef.current = parsedParagraphs;

        if (previewContainerRef.current) {
          await renderDocxFromZip(zip);
          hasPendingRenderRef.current = false;
        } else {
          // Preview container not mounted yet — render later.
          hasPendingRenderRef.current = true;
        }
      } catch (error) {
        toast.error(`Failed to load.`);
        console.error(error);
      } finally {
        setIsBusy(false);
      }
    },
    [renderDocxFromZip, clearUndoRedoHistory],
  );

  // Render deferred preview once the container mounts.
  useEffect(() => {
    if (hasPendingRenderRef.current && previewContainerRef.current && workingZipRef.current) {
      hasPendingRenderRef.current = false;
      renderDocxFromZip(workingZipRef.current);
    }
  });

  /* ══════════════════════════════════════════════════════════════
   *  loadIssues — Load and resolve style-guide issues
   *
   *  Two paths:
   *
   *  FRESH SESSION (first analysis):
   *    Gemini RESPONSE.issues → computeOffsets() → initializeIssueAnchors()
   *    Result stored in canonicalIssuesRef for persistence.
   *
   *  RESUMED SESSION (returning to saved work):
   *    RESOLVED_ISSUES (from DB) + ISSUE_STATUSES (from DB)
   *    → apply saved statuses → replay accepted edits on original doc
   *    → rebuild anchors against current text
   *    No recomputation needed — uses saved stable IDs directly.
   * ══════════════════════════════════════════════════════════════ */

  const loadIssues = useCallback(
    async (session) => {
      const currentParagraphsList = paragraphsRef.current;
      const zip = workingZipRef.current;
      if (!currentParagraphsList.length || !zip) {
        console.warn('[loadIssues] paragraphs or zip not ready yet');
        return;
      }

      clearUndoRedoHistory();

      try {
        const savedIssues = session?.RESOLVED_ISSUES;
        const savedStatuses = session?.ISSUE_STATUSES;

        if (savedIssues?.length && savedStatuses) {
          /* ── Resumed session path ── */
          canonicalIssuesRef.current = savedIssues;

          // Re-apply saved status to every persisted issue.
          const issuesWithStatuses = savedIssues.map((issue) => ({
            ...issue,
            status: savedStatuses[issue.id] || 'open',
          }));

          // Replay accepted edits into the original document XML.
          // Process in reverse document order so earlier replacements
          // don't shift the offsets of later ones.
          const issuesToReplay = issuesWithStatuses
            .filter((issue) => issue.status === 'accepted')
            .sort((a, b) => b.paragraphId - a.paragraphId || b.start - a.start);

          let workingParagraphs = currentParagraphsList;

          if (issuesToReplay.length) {
            let workingXml = await getTextFromZip(zip, DOC_XML_PATH);

            for (const issue of issuesToReplay) {
              try {
                const { xmlDoc, textNodesByParagraph, offsetMap } =
                  parseDocXmlToParagraphs(workingXml);

                workingXml = applyReplacementInParagraph({
                  xmlDoc,
                  textNodesByParagraph,
                  offsetMap,
                  paragraphId: issue.paragraphId,
                  startOffset: issue.start,
                  endOffset: issue.end,
                  replacementText: issue.suggested,
                });
              } catch (error) {
                console.warn(`[loadIssues replay] Error on issue ${issue.id}:`, error.message);
              }
            }

            // Commit replayed XML to zip and update state.
            setTextInZip(zip, DOC_XML_PATH, workingXml);
            const { paragraphs: paragraphsAfterReplay } = parseDocXmlToParagraphs(workingXml);
            setCurrentParagraphs(paragraphsAfterReplay);
            paragraphsRef.current = paragraphsAfterReplay;
            setCurrentDocXml(workingXml);
            workingParagraphs = paragraphsAfterReplay;

            await renderDocxFromZip(zip);
          }

          // Rebuild anchors against the CURRENT paragraph text.
          // Uses cursor tracking so duplicate phrases resolve left-to-right.
          const perParagraphCursors = new Map();

          const issuesWithAnchors = issuesWithStatuses.map((issue) => {
            const paragraphText = workingParagraphs[issue.paragraphId]?.text || '';

            // Rejected/stale issues don't need anchors.
            if (issue.status === 'rejected' || issue.status === 'stale') {
              return { ...issue, anchorOpen: null, anchorAccepted: null };
            }

            // Accepted issues anchor on their suggested text; open on original.
            const needleText = issue.status === 'accepted' ? issue.suggested : issue.original;
            const cursorPosition = perParagraphCursors.get(issue.paragraphId) || 0;
            const foundIndex = paragraphText.indexOf(needleText, cursorPosition);

            if (foundIndex !== -1) {
              const foundEnd = foundIndex + needleText.length;
              perParagraphCursors.set(issue.paragraphId, foundEnd);

              return issue.status === 'accepted'
                ? {
                    ...issue,
                    start: foundIndex,
                    end: foundEnd,
                    anchorAccepted: buildAnchor(
                      paragraphText,
                      foundIndex,
                      foundEnd,
                      issue.suggested,
                    ),
                    anchorOpen: null,
                  }
                : {
                    ...issue,
                    start: foundIndex,
                    end: foundEnd,
                    anchorOpen: buildAnchor(paragraphText, foundIndex, foundEnd, issue.original),
                    anchorAccepted: null,
                  };
            }

            // Could not locate text in current paragraph.
            return { ...issue, anchorOpen: null, anchorAccepted: null };
          });

          // Initialize UI with the rebuilt issue list.
          setIssues(issuesWithAnchors);
          const firstVisibleIssue = issuesWithAnchors.find(
            (issue) => issue.status === 'open' || issue.status === 'accepted',
          );
          setSelectedIssueId(firstVisibleIssue?.id || null);
          setIsPopoverOpen(!!firstVisibleIssue);
        } else {
          /* ── Fresh session path ── */
          const documentXml = await getTextFromZip(zip, DOC_XML_PATH);
          const { textNodesByParagraph, offsetMap } = parseDocXmlToParagraphs(documentXml);

          // Resolve Gemini's raw issues to concrete character offsets.
          const resolvedIssues = computeOffsets(
            session?.RESPONSE?.issues,
            currentParagraphsList,
            textNodesByParagraph,
            offsetMap,
          );

          // Add anchor metadata for highlight positioning.
          const issuesWithAnchors = initializeIssueAnchors(resolvedIssues, currentParagraphsList);

          // Save as canonical baseline for persistence and undoAll.
          canonicalIssuesRef.current = issuesWithAnchors;

          setIssues(issuesWithAnchors);
          const firstIssueId = issuesWithAnchors[0]?.id || null;
          setSelectedIssueId(firstIssueId);
          setIsPopoverOpen(!!firstIssueId);
        }
      } catch (error) {
        toast.error(`Failed to load issues`);
        console.error(error);
      }
    },
    [renderDocxFromZip, clearUndoRedoHistory],
  );

  // Expose loadFile and loadIssues to the parent component via ref.
  useImperativeHandle(ref, () => ({ loadFile, loadIssues }), [loadFile, loadIssues]);

  /* ══════════════════════════════════════════════════════════════
   *  Issue selection and navigation
   * ══════════════════════════════════════════════════════════════ */

  /** Hide the popover and restore focus to the selected tracking item. */
  const closePopover = useCallback(
    (options = {}) => {
      const { clearSelection = false } = options;
      const issueIdToRestoreFocus = selectedIssueId;

      setIsPopoverOpen(false);
      if (clearSelection) {
        setSelectedIssueId(null);
      }
      setPopoverAnchorElement(null);
      // Remove selected styling from all highlight spans.
      for (const [, highlightElement] of highlightMapRef.current.entries()) {
        applySelectedStyle(highlightElement, false);
      }

      requestAnimationFrame(() => {
        const trackingItem = document.querySelector(
          `[data-tracking-id="${issueIdToRestoreFocus}"]`,
        );

        trackingItem?.focus();
      });
    },
    [selectedIssueId],
  );

  /**
   * Select an issue by ID: update selection state, switch to the correct
   * tab, position the popover, and scroll the highlight into view.
   */
  const selectIssue = useCallback(
    (issueId) => {
      setSelectedIssueId(issueId);

      // Auto-switch tab to match the issue's status.
      const targetIssue = issues.find((issue) => issue.id === issueId);
      if (targetIssue) {
        if (targetIssue.status === 'accepted') setActiveTab('changes');
        else if (targetIssue.status === 'open' || targetIssue.status === 'stale') {
          setActiveTab('suggestions');
        }
      }

      // Position popover on the highlight span (if it exists in the DOM).
      const highlightElement = highlightMapRef.current.get(issueId) || null;
      setPopoverAnchorElement(highlightElement);
      setIsPopoverOpen(!!highlightElement);
      highlightSelected(issueId, highlightMapRef.current);
      if (highlightElement) {
        highlightElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    },
    [issues],
  );

  /**
   * Navigate to the previous or next issue in a collection (with wrap-around).
   * Used by the popover's arrow buttons.
   */
  const changeSelection = useCallback(
    (issueCollection, direction) => {
      if (!issueCollection.length) return;
      const currentIndex = issueCollection.findIndex((issue) => issue.id === selectedIssueId);
      const nextIndex =
        currentIndex === -1
          ? 0
          : direction === 'next'
            ? (currentIndex + 1) % issueCollection.length
            : (currentIndex - 1 + issueCollection.length) % issueCollection.length;
      selectIssue(issueCollection[nextIndex].id);
    },
    [selectIssue, selectedIssueId],
  );

  /**
   * After an issue is removed from a filtered list (accept/dismiss/revert),
   * find the next issue to auto-advance to.
   * Returns the next item's ID, or previous if it was last, or null if empty.
   */
  const getNextIssueId = useCallback(
    (currentId, filterFunction) => {
      const filteredList = issues.filter(filterFunction);
      const currentIndex = filteredList.findIndex((issue) => issue.id === currentId);
      if (currentIndex === -1 || filteredList.length <= 1) return null;
      const nextIndex =
        currentIndex < filteredList.length - 1 ? currentIndex + 1 : currentIndex - 1;
      return filteredList[nextIndex]?.id || null;
    },
    [issues],
  );

  /* ══════════════════════════════════════════════════════════════
   *  Accept single issue
   *
   *  Flow: validate text → apply XML replacement → push undo →
   *        update sibling offsets → rebuild anchors → re-render
   * ══════════════════════════════════════════════════════════════ */

  const acceptIssue = useCallback(
    async (issueId) => {
      const zip = workingZipRef.current;
      if (!zip) return;
      const targetIssue = issues.find((issue) => issue.id === issueId);
      if (!targetIssue || targetIssue.status !== 'open') return;

      // Pre-compute auto-advance target BEFORE this issue leaves the open list.
      const nextOpenIssueId = getNextIssueId(issueId, (issue) => issue.status === 'open');

      setIsBusy(true);

      try {
        // ── Step 1: Parse current XML and validate text at stored offsets ──
        const currentXml = await getTextFromZip(zip, DOC_XML_PATH);
        const {
          xmlDoc,
          paragraphs: parsedParagraphs,
          textNodesByParagraph,
          offsetMap,
        } = parseDocXmlToParagraphs(currentXml);

        const paragraph = parsedParagraphs[targetIssue.paragraphId];
        if (!paragraph) throw new Error('Paragraph missing');

        const textAtExpectedPosition = (paragraph.text || '').slice(
          targetIssue.start,
          targetIssue.end,
        );
        if (textAtExpectedPosition !== targetIssue.original) {
          // Text has shifted or been modified — mark this issue as stale.
          setIssues((previousIssues) =>
            previousIssues.map((issue) =>
              issue.id === issueId ? { ...issue, status: 'stale' } : issue,
            ),
          );
          toast.error(
            `Text has changed — expected "${previewShort(targetIssue.original)}" but found "${previewShort(textAtExpectedPosition)}". Issue marked stale.`,
          );
          return;
        }

        // ── Step 2: Apply the replacement in the XML ──
        const updatedXml = applyReplacementInParagraph({
          xmlDoc,
          textNodesByParagraph,
          offsetMap,
          paragraphId: targetIssue.paragraphId,
          startOffset: targetIssue.start,
          endOffset: targetIssue.end,
          replacementText: targetIssue.suggested,
        });

        // ── Step 3: Push undo snapshot (AFTER validation, BEFORE state updates) ──
        pushUndoSnapshot();

        // ── Step 4: Commit XML and update all issue offsets ──
        setTextInZip(zip, DOC_XML_PATH, updatedXml);
        const { paragraphs: paragraphsAfterEdit } = parseDocXmlToParagraphs(updatedXml);
        const shiftedIssues = updateIssuesAfterAccept(issues, targetIssue, paragraphsAfterEdit);
        const issuesWithFreshAnchors = rebuildOpenAnchors(shiftedIssues, paragraphsAfterEdit);

        setCurrentDocXml(updatedXml);
        setCurrentParagraphs(paragraphsAfterEdit);
        paragraphsRef.current = paragraphsAfterEdit;
        setIssues(issuesWithFreshAnchors);
        debouncedRender(zip);

        // ── Step 5: Auto-advance to next open issue ──
        if (nextOpenIssueId) {
          setSelectedIssueId(nextOpenIssueId);
          setIsPopoverOpen(true);
        } else {
          closePopover({ clearSelection: true });
        }
      } catch (error) {
        toast.error(`Accept failed: ${error.message}`);
        console.error(error);
      } finally {
        setIsBusy(false);
      }
    },
    [issues, debouncedRender, getNextIssueId, closePopover, pushUndoSnapshot],
  );

  /* ══════════════════════════════════════════════════════════════
   *  Accept all open issues
   *
   *  Applies every open issue in reverse document order (so earlier
   *  offsets stay stable), then rebuilds anchors using cumulative-delta
   *  estimation to correctly locate each accepted issue's suggested
   *  text — even when common strings like " and" appear multiple times.
   * ══════════════════════════════════════════════════════════════ */

  const acceptAll = useCallback(async () => {
    const zip = workingZipRef.current;
    if (!zip) return;
    const openIssuesList = issues.filter((issue) => issue.status === 'open');
    if (!openIssuesList.length) return;
    setIsBusy(true);

    try {
      // Sort end-to-start so earlier offsets remain stable during iteration.
      const sortedIssues = [...openIssuesList].sort(
        (a, b) => b.paragraphId - a.paragraphId || b.start - a.start,
      );

      let workingXml = await getTextFromZip(zip, DOC_XML_PATH);
      const successfullyAcceptedIds = new Set();

      for (const issue of sortedIssues) {
        try {
          const {
            xmlDoc,
            paragraphs: parsedParagraphs,
            textNodesByParagraph,
            offsetMap,
          } = parseDocXmlToParagraphs(workingXml);

          const paragraph = parsedParagraphs[issue.paragraphId];
          if (!paragraph) continue;

          // Mutable coordinates that may be recovered if drifted.
          let { start: issueStart, end: issueEnd } = issue;
          const textAtPosition = (paragraph.text || '').slice(issueStart, issueEnd);

          if (textAtPosition !== issue.original) {
            // ── Text recovery: try to find the original text nearby ──
            const paragraphText = paragraph.text || '';
            let wasRecovered = false;

            // Recovery strategy 1: nearby search (±100 chars).
            const nearbySearchStart = Math.max(0, issueStart - 100);
            const nearbyIndex = paragraphText.indexOf(issue.original, nearbySearchStart);
            if (nearbyIndex !== -1 && Math.abs(nearbyIndex - issueStart) <= 100) {
              issueStart = nearbyIndex;
              issueEnd = nearbyIndex + issue.original.length;
              wasRecovered = true;
            }

            // Recovery strategy 2: anchor context search.
            if (!wasRecovered && issue.anchorOpen?.before) {
              const beforeContextIndex = paragraphText.indexOf(issue.anchorOpen.before);
              if (beforeContextIndex !== -1) {
                const afterContextPosition = beforeContextIndex + issue.anchorOpen.before.length;
                const contextNearbyIndex = paragraphText.indexOf(
                  issue.original,
                  afterContextPosition,
                );
                if (contextNearbyIndex !== -1 && contextNearbyIndex - afterContextPosition < 5) {
                  issueStart = contextNearbyIndex;
                  issueEnd = contextNearbyIndex + issue.original.length;
                  wasRecovered = true;
                }
              }
            }

            if (!wasRecovered) {
              console.warn(
                `[acceptAll] Skipping "${previewShort(issue.original)}" — not found near expected position`,
              );
              continue;
            }
          }

          workingXml = applyReplacementInParagraph({
            xmlDoc,
            textNodesByParagraph,
            offsetMap,
            paragraphId: issue.paragraphId,
            startOffset: issueStart,
            endOffset: issueEnd,
            replacementText: issue.suggested,
          });
          successfullyAcceptedIds.add(issue.id);
        } catch (error) {
          console.warn(`[acceptAll] Skipping issue ${issue.id}:`, error.message);
        }
      }

      if (!successfullyAcceptedIds.size) {
        toast.error('No issues could be applied.');
        return;
      }

      // Push undo AFTER confirming at least one issue was applied.
      pushUndoSnapshot();

      setTextInZip(zip, DOC_XML_PATH, workingXml);
      const { paragraphs: paragraphsAfterBatch } = parseDocXmlToParagraphs(workingXml);
      const paragraphTextLookup = new Map(
        paragraphsAfterBatch.map((paragraph) => [paragraph.paragraphId, paragraph.text || '']),
      );

      /*
       * ── Rebuild anchors for accepted issues using cumulative-delta estimation ──
       *
       * After bulk-accepting, each accepted issue's suggested text has shifted
       * from its original position by the sum of all earlier replacements in the
       * same paragraph. We track this cumulative delta to search at the right spot.
       *
       * This avoids the naive cursor-scan bug where common text like " and"
       * matches the wrong occurrence.
       */
      const acceptedInDocOrder = issues
        .filter((issue) => successfullyAcceptedIds.has(issue.id))
        .sort((a, b) => a.paragraphId - b.paragraphId || a.start - b.start);

      const issuesByParagraph = new Map();
      for (const issue of acceptedInDocOrder) {
        if (!issuesByParagraph.has(issue.paragraphId)) {
          issuesByParagraph.set(issue.paragraphId, []);
        }
        issuesByParagraph.get(issue.paragraphId).push(issue);
      }

      const acceptedAnchorPatches = new Map();

      for (const [paragraphId, paragraphIssues] of issuesByParagraph) {
        const paragraphText = paragraphTextLookup.get(paragraphId) || '';
        let cumulativeDelta = 0;

        for (const issue of paragraphIssues) {
          const expectedStart = issue.start + cumulativeDelta;
          const issueDelta = issue.suggested.length - issue.original.length;
          cumulativeDelta += issueDelta;

          // Search near the expected position (not from 0).
          const searchStart = Math.max(0, expectedStart - 30);
          const foundIndex = paragraphText.indexOf(issue.suggested, searchStart);

          if (foundIndex === -1 || Math.abs(foundIndex - expectedStart) > 80) {
            acceptedAnchorPatches.set(issue.id, { status: 'accepted', anchorAccepted: null });
            continue;
          }

          const foundEnd = foundIndex + issue.suggested.length;
          acceptedAnchorPatches.set(issue.id, {
            status: 'accepted',
            start: foundIndex,
            end: foundEnd,
            anchorAccepted: buildAnchor(paragraphText, foundIndex, foundEnd, issue.suggested),
          });
        }
      }

      // Update remaining open issues whose offsets may have drifted.
      const updatedIssues = issues.map((issue) => {
        const patch = acceptedAnchorPatches.get(issue.id);
        if (patch) return { ...issue, ...patch };

        if (issue.status === 'open') {
          const paragraphText = paragraphTextLookup.get(issue.paragraphId) || '';
          const textAtPosition = paragraphText.slice(issue.start, issue.end);

          if (textAtPosition !== issue.original) {
            // Offset drifted — try nearby search.
            const searchStart = Math.max(0, issue.start - 100);
            const nearbyIndex = paragraphText.indexOf(issue.original, searchStart);
            if (nearbyIndex !== -1 && Math.abs(nearbyIndex - issue.start) <= 150) {
              const nearbyEnd = nearbyIndex + issue.original.length;
              return {
                ...issue,
                start: nearbyIndex,
                end: nearbyEnd,
                anchorOpen: buildAnchor(paragraphText, nearbyIndex, nearbyEnd, issue.original),
              };
            }
            return { ...issue, status: 'stale', anchorOpen: null };
          }
        }

        return issue;
      });

      const issuesWithFreshAnchors = rebuildOpenAnchors(updatedIssues, paragraphsAfterBatch);
      setCurrentDocXml(workingXml);
      setCurrentParagraphs(paragraphsAfterBatch);
      paragraphsRef.current = paragraphsAfterBatch;
      setIssues(issuesWithFreshAnchors);
      closePopover({ clearSelection: true });
      await renderDocxFromZip(zip);
    } catch (error) {
      toast.error(`Accept all failed: ${error.message}`);
      console.error(error);
    } finally {
      setIsBusy(false);
    }
  }, [issues, closePopover, renderDocxFromZip, pushUndoSnapshot]);

  /* ══════════════════════════════════════════════════════════════
   *  Dismiss single issue
   *
   *  Marks one issue as rejected (no XML change needed).
   * ══════════════════════════════════════════════════════════════ */

  const dismissIssue = useCallback(
    (issueId) => {
      pushUndoSnapshot();

      const nextOpenIssueId = getNextIssueId(issueId, (issue) => issue.status === 'open');

      setIssues((previousIssues) =>
        previousIssues.map((issue) =>
          issue.id === issueId ? { ...issue, status: 'rejected' } : issue,
        ),
      );

      if (nextOpenIssueId) {
        setSelectedIssueId(nextOpenIssueId);
        setIsPopoverOpen(true);
      } else {
        closePopover({ clearSelection: true });
      }
    },
    [closePopover, getNextIssueId, pushUndoSnapshot],
  );

  /* ══════════════════════════════════════════════════════════════
   *  Revert accepted issue
   *
   *  Replaces the suggested text back with the original text.
   *  Validates that suggested text still exists at stored offsets
   *  before reverting to prevent document corruption.
   * ══════════════════════════════════════════════════════════════ */

  const revertIssue = useCallback(
    async (issueId) => {
      const zip = workingZipRef.current;
      if (!zip) return;
      const targetIssue = issues.find((issue) => issue.id === issueId);
      if (!targetIssue || targetIssue.status !== 'accepted') return;

      const nextAcceptedIssueId = getNextIssueId(issueId, (issue) => issue.status === 'accepted');

      setIsBusy(true);

      try {
        // ── Step 1: Parse current XML and validate ──
        const currentXml = await getTextFromZip(zip, DOC_XML_PATH);
        const {
          xmlDoc,
          paragraphs: parsedParagraphs,
          textNodesByParagraph,
          offsetMap,
        } = parseDocXmlToParagraphs(currentXml);

        const paragraph = parsedParagraphs[targetIssue.paragraphId];
        if (!paragraph) throw new Error('Paragraph missing');

        const textAtExpectedPosition = (paragraph.text || '').slice(
          targetIssue.start,
          targetIssue.end,
        );
        if (textAtExpectedPosition !== targetIssue.suggested) {
          setIssues((previousIssues) =>
            previousIssues.map((issue) =>
              issue.id === issueId ? { ...issue, status: 'stale' } : issue,
            ),
          );
          toast.error(
            `Cannot revert — expected "${previewShort(targetIssue.suggested)}" but found "${previewShort(textAtExpectedPosition)}". Issue marked stale.`,
          );
          return;
        }

        // ── Step 2: Apply the revert (suggested → original) ──
        const revertedXml = applyReplacementInParagraph({
          xmlDoc,
          textNodesByParagraph,
          offsetMap,
          paragraphId: targetIssue.paragraphId,
          startOffset: targetIssue.start,
          endOffset: targetIssue.end,
          replacementText: targetIssue.original,
        });

        // ── Step 3: Push undo snapshot (AFTER validation) ──
        pushUndoSnapshot();

        // ── Step 4: Commit and shift sibling offsets ──
        setTextInZip(zip, DOC_XML_PATH, revertedXml);
        const { paragraphs: paragraphsAfterRevert } = parseDocXmlToParagraphs(revertedXml);
        const paragraphText = paragraphsAfterRevert[targetIssue.paragraphId]?.text || '';
        const revertLengthDelta = targetIssue.original.length - targetIssue.suggested.length;
        const revertedEnd = targetIssue.start + targetIssue.original.length;

        const updatedIssues = issues.map((issue) => {
          // The reverted issue goes back to 'open' status.
          if (issue.id === targetIssue.id) {
            return {
              ...issue,
              status: 'open',
              start: targetIssue.start,
              end: revertedEnd,
              anchorOpen: buildAnchor(
                paragraphText,
                targetIssue.start,
                revertedEnd,
                targetIssue.original,
              ),
              anchorAccepted: null,
            };
          }
          // Issues before the revert or in other paragraphs are unchanged.
          if (issue.paragraphId !== targetIssue.paragraphId || issue.start < targetIssue.start) {
            return issue;
          }
          // Shift issues after the revert by the length delta.
          const shiftedStart = issue.start + revertLengthDelta;
          const shiftedEnd = issue.end + revertLengthDelta;
          if (issue.status === 'accepted') {
            return {
              ...issue,
              start: shiftedStart,
              end: shiftedEnd,
              anchorAccepted: buildAnchor(paragraphText, shiftedStart, shiftedEnd, issue.suggested),
            };
          }
          if (issue.status === 'open') {
            return {
              ...issue,
              start: shiftedStart,
              end: shiftedEnd,
              anchorOpen: buildAnchor(paragraphText, shiftedStart, shiftedEnd, issue.original),
            };
          }
          return issue;
        });

        const issuesWithFreshAnchors = rebuildOpenAnchors(updatedIssues, paragraphsAfterRevert);
        setCurrentDocXml(revertedXml);
        setCurrentParagraphs(paragraphsAfterRevert);
        paragraphsRef.current = paragraphsAfterRevert;
        setIssues(issuesWithFreshAnchors);
        debouncedRender(zip);

        // Auto-advance to next accepted issue.
        if (nextAcceptedIssueId) {
          setSelectedIssueId(nextAcceptedIssueId);
          setIsPopoverOpen(true);
        } else {
          closePopover({ clearSelection: true });
        }
      } catch (error) {
        toast.error(`Revert failed: ${error.message}`);
        console.error(error);
      } finally {
        setIsBusy(false);
      }
    },
    [issues, debouncedRender, getNextIssueId, closePopover, pushUndoSnapshot],
  );

  /* ══════════════════════════════════════════════════════════════
   *  Save session to backend (reusable)
   * ══════════════════════════════════════════════════════════════ */

  const saveSession = useCallback(async () => {
    const issueStatusMap = {};
    for (const issue of issues) {
      issueStatusMap[issue.id] = issue.status;
    }

    await patchData(
      esga.updateSession(props.sessionData?.SESSION_ID),
      {
        resolvedIssues: canonicalIssuesRef.current,
        issueStatuses: issueStatusMap,
      },
      true,
    );

    await props.fetchSessionHistory();
  }, [props.sessionData, issues]);

  /* ══════════════════════════════════════════════════════════════
   *  Download — save session to backend + export DOCX
   * ══════════════════════════════════════════════════════════════ */

  const doDownload = useCallback(async () => {
    const zip = workingZipRef.current;
    if (!zip || !loadedFile) return;
    const filename = 'odu_' + props.sessionData?.DOC_NAME?.split('/').pop();
    setIsBusy(true);
    try {
      await saveSession();
    } catch (error) {
      toast.error('Failed to save session.');
    }
    try {
      await downloadZipAsDocx(zip, filename);
    } finally {
      setIsBusy(false);
    }
  }, [loadedFile, props.sessionData, saveSession]);

  /* ══════════════════════════════════════════════════════════════
   *  Manual save
   * ══════════════════════════════════════════════════════════════ */

  const handleSave = useCallback(async () => {
    setIsBusy(true);
    try {
      await saveSession();
      toast.success('Changes saved.');
    } catch (error) {
      toast.error('Failed to save changes.');
    } finally {
      setIsBusy(false);
    }
  }, [saveSession]);

  /* ══════════════════════════════════════════════════════════════
   *  Auto-save (10 s after last change, when enabled)
   * ══════════════════════════════════════════════════════════════ */

  const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(true);
  const autoSaveTimerRef = useRef(null);

  // Reset the auto-save timer whenever issues change and auto-save is on.
  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    if (!isAutoSaveEnabled || !props.sessionData?.SESSION_ID) return;

    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        await saveSession();
      } catch {
        toast.error('Changes auto-save failed.');
      }
    }, ESGA_AUTO_SAVE_INTERVAL);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [issues, isAutoSaveEnabled, saveSession, props.sessionData?.SESSION_ID]);

  doDownloadRef.current = doDownload;

  /** Guarded download: warn user if there are unresolved issues. */
  const handleDownload = useCallback(async () => {
    if (hasUnresolvedIssues) {
      setIsPendingDownload(true);
      setIsWarningModalOpen(true);
      return;
    }
    await doDownload();
  }, [hasUnresolvedIssues, doDownload]);

  /* ══════════════════════════════════════════════════════════════
   *  Dismiss all / Undo all (bulk operations)
   * ══════════════════════════════════════════════════════════════ */

  /** Mark all open/stale issues as rejected. */
  const dismissAll = useCallback(() => {
    pushUndoSnapshot();
    setIssues((previousIssues) =>
      previousIssues.map((issue) =>
        issue.status === 'open' || issue.status === 'stale'
          ? { ...issue, status: 'rejected' }
          : issue,
      ),
    );
    closePopover({ clearSelection: true });
  }, [closePopover, pushUndoSnapshot]);

  /**
   * Revert ALL accepted changes back to original text.
   *
   * Processes accepted issues in reverse order. If all succeed,
   * resets from canonicalIssuesRef (clean state). If some fail,
   * only resets successfully-reverted issues; failed ones stay
   * accepted with rebuilt anchors against the partially-reverted doc.
   */
  const undoAll = useCallback(async () => {
    const zip = workingZipRef.current;
    if (!zip) return;

    const currentAcceptedIssues = issues.filter((issue) => issue.status === 'accepted');
    if (!currentAcceptedIssues.length) return;

    pushUndoSnapshot();
    setIsBusy(true);
    closePopover({ clearSelection: true });

    try {
      const sortedAccepted = [...currentAcceptedIssues].sort(
        (a, b) => b.paragraphId - a.paragraphId || b.start - a.start,
      );

      let workingXml = await getTextFromZip(zip, DOC_XML_PATH);
      const successfullyRevertedIds = new Set();

      for (const issue of sortedAccepted) {
        try {
          const {
            xmlDoc,
            paragraphs: parsedParagraphs,
            textNodesByParagraph,
            offsetMap,
          } = parseDocXmlToParagraphs(workingXml);

          const paragraph = parsedParagraphs[issue.paragraphId];
          if (!paragraph) {
            console.warn(
              `[undoAll] Paragraph ${issue.paragraphId} missing, skipping issue ${issue.id}`,
            );
            continue;
          }

          const textAtPosition = (paragraph.text || '').slice(issue.start, issue.end);
          if (textAtPosition !== issue.suggested) {
            console.warn(
              `[undoAll] Text mismatch for issue ${issue.id}: expected "${issue.suggested?.slice(0, 30)}" at [${issue.start}:${issue.end}], found "${textAtPosition?.slice(0, 30)}"`,
            );
            continue;
          }

          workingXml = applyReplacementInParagraph({
            xmlDoc,
            textNodesByParagraph,
            offsetMap,
            paragraphId: issue.paragraphId,
            startOffset: issue.start,
            endOffset: issue.end,
            replacementText: issue.original,
          });
          successfullyRevertedIds.add(issue.id);
        } catch (error) {
          console.warn(`[undoAll] Skipping revert for issue ${issue.id}:`, error.message);
        }
      }

      // Commit the (partially or fully) reverted XML.
      setTextInZip(zip, DOC_XML_PATH, workingXml);
      const { paragraphs: paragraphsAfterRevert } = parseDocXmlToParagraphs(workingXml);

      if (canonicalIssuesRef.current) {
        const currentStatusMap = new Map(issues.map((issue) => [issue.id, issue.status]));

        if (successfullyRevertedIds.size === currentAcceptedIssues.length) {
          // ALL reverts succeeded — doc is back to original state.
          // Reset from canonical baseline (stable IDs, original-doc offsets).
          const resetIssues = canonicalIssuesRef.current.map((issue) => ({
            ...issue,
            status: currentStatusMap.get(issue.id) === 'rejected' ? 'rejected' : 'open',
            anchorAccepted: null,
          }));
          setIssues(resetIssues);
        } else {
          // PARTIAL revert — some issues failed. Reset what we can.
          const paragraphTextLookup = new Map(
            paragraphsAfterRevert.map((paragraph) => [paragraph.paragraphId, paragraph.text || '']),
          );

          const partialResetIssues = canonicalIssuesRef.current.map((issue) => {
            const currentStatus = currentStatusMap.get(issue.id);

            // Successfully reverted → reopen.
            if (successfullyRevertedIds.has(issue.id)) {
              return { ...issue, status: 'open', anchorAccepted: null };
            }

            // Already rejected → stay rejected.
            if (currentStatus === 'rejected') {
              return { ...issue, status: 'rejected', anchorAccepted: null };
            }

            // Was open (never accepted) → stay open.
            if (currentStatus === 'open') {
              return { ...issue, anchorAccepted: null };
            }

            // Failed to revert (still accepted) → rebuild anchor in current doc.
            if (currentStatus === 'accepted') {
              const paragraphText = paragraphTextLookup.get(issue.paragraphId) || '';
              const textAtPosition = paragraphText.slice(issue.start, issue.end);
              if (textAtPosition === issue.suggested) {
                return {
                  ...issue,
                  anchorAccepted: buildAnchor(
                    paragraphText,
                    issue.start,
                    issue.end,
                    issue.suggested,
                  ),
                };
              }
              // Offset drifted — try to find suggested text.
              const foundIndex = paragraphText.indexOf(issue.suggested);
              if (foundIndex !== -1) {
                const foundEnd = foundIndex + issue.suggested.length;
                return {
                  ...issue,
                  start: foundIndex,
                  end: foundEnd,
                  anchorAccepted: buildAnchor(paragraphText, foundIndex, foundEnd, issue.suggested),
                };
              }
              // Can't find it — mark stale.
              return { ...issue, status: 'stale', anchorAccepted: null };
            }

            return issue;
          });
          setIssues(partialResetIssues);
          toast.error('Some changes could not be reverted.');
        }
      } else {
        // No canonical baseline available — clear issues defensively.
        setIssues([]);
      }

      // Sync all state after bulk revert.
      setCurrentDocXml(workingXml);
      setCurrentParagraphs(paragraphsAfterRevert);
      paragraphsRef.current = paragraphsAfterRevert;
      setSelectedIssueId(null);

      await renderDocxFromZip(zip);
    } catch (error) {
      toast.error(`Undo all failed: ${error.message}`);
      console.error(error);
    } finally {
      setIsBusy(false);
    }
  }, [issues, renderDocxFromZip, closePopover, pushUndoSnapshot]);

  /* ══════════════════════════════════════════════════════════════
   *  Effects — side effects that react to state changes
   * ══════════════════════════════════════════════════════════════ */

  /**
   * EFFECT: Re-apply highlight overlays whenever issues, paragraphs,
   * or preview DOM changes. Also restores selection styling if an
   * issue is currently selected.
   */
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;

    const highlightElementMap = applyHighlightsToPreview(
      container,
      currentParagraphs,
      issues,
      issueNumberById,
    );
    highlightMapRef.current = highlightElementMap;

    if (selectedIssueId) {
      const selectedHighlight = highlightElementMap.get(selectedIssueId) || null;
      setPopoverAnchorElement(selectedHighlight);
      if (selectedHighlight) {
        highlightSelected(selectedIssueId, highlightElementMap);
        selectedHighlight.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        requestAnimationFrame(() => popperRef.current?.update?.());
      }
    }
  }, [issues, currentParagraphs, previewRenderKey, issueNumberById, selectedIssueId]);

  /**
   * EFFECT: selecting a highlight span in the preview opens its popover.
   */
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;

    const selectPreviewHighlight = (event) => {
      const clickedHighlight = event.target?.closest?.('[data-issue-id]');
      if (!clickedHighlight) return;
      const clickedIssueId = clickedHighlight.getAttribute('data-issue-id');
      if (clickedIssueId) selectIssue(clickedIssueId);
    };

    const handlePreviewKeyDown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const focusedHighlight = event.target?.closest?.('[data-issue-id]');
      if (!focusedHighlight) return;
      event.preventDefault();
      selectPreviewHighlight(event);
    };

    container.addEventListener('click', selectPreviewHighlight);
    container.addEventListener('keydown', handlePreviewKeyDown);
    return () => {
      container.removeEventListener('click', selectPreviewHighlight);
      container.removeEventListener('keydown', handlePreviewKeyDown);
    };
  }, [selectIssue, previewRenderKey]);

  /**
   * EFFECT: Reposition the floating popover when the user scrolls or resizes.
   */
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;

    const repositionPopover = () => {
      if (isPopoverOpen) popperRef.current?.update?.();
    };

    container.addEventListener('scroll', repositionPopover, { passive: true });
    window.addEventListener('scroll', repositionPopover, true);
    window.addEventListener('resize', repositionPopover);
    return () => {
      container.removeEventListener('scroll', repositionPopover);
      window.removeEventListener('scroll', repositionPopover, true);
      window.removeEventListener('resize', repositionPopover);
    };
  }, [isPopoverOpen, previewRenderKey]);

  /**
   * EFFECT: Cleanup — cancel any pending debounced render on unmount.
   */
  useEffect(() => {
    return () => {
      if (renderTimerRef.current) clearTimeout(renderTimerRef.current);
    };
  }, []);

  /* ══════════════════════════════════════════════════════════════
   *  Render — component JSX
   * ══════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ height: '100%', minHeight: 0 }}>
      <Grid
        container
        columnSpacing={2}
        rowSpacing={0}
        sx={{
          height: '100%',
          minHeight: 0,
        }}
      >
        {/* ── Tracking Panel (issue list + action buttons) ── */}
        <Grid
          item
          className={`trackingPanel${trackingMinimized ? ' minimized' : ''}`}
          xs={12}
          xl={3}
          order={{ xs: 2, xl: 1 }}
          sx={{ height: '100%', minHeight: 0 }}
        >
          <TrackingPanel
            tab={activeTab}
            selectedIssueId={selectedIssueId}
            issues={issues}
            dismissAll={dismissAll}
            undoAll={undoAll}
            setPopoverOpen={setIsPopoverOpen}
            setSelectedAnchorEl={setPopoverAnchorElement}
            busy={isBusy}
            file={loadedFile}
            selectIssue={selectIssue}
            setTab={setActiveTab}
            issueNumberById={issueNumberById}
            highlightMapRef={highlightMapRef}
            previewShort={previewShort}
            acceptAll={acceptAll}
            isLoading={props.isLoading}
            minimized={trackingMinimized}
            onMinimizeToggle={setTrackingMinimized}
          />
        </Grid>

        {/* ── Preview Panel (document render + undo/redo/download) ── */}
        <Grid
          item
          xs={12}
          lg={12}
          xl={9}
          order={{ xs: 1, xl: 2 }}
          sx={{ height: '100%', minHeight: 0 }}
        >
          <PreviewPanel
            reanalyze={() => {}}
            previewRef={previewContainerRef}
            handleDownload={handleDownload}
            handleSave={handleSave}
            isAutoSaveEnabled={isAutoSaveEnabled}
            onAutoSaveToggle={setIsAutoSaveEnabled}
            originalBuffer={originalFileBuffer}
            zip={workingZipRef.current}
            busy={isBusy}
            isLoading={props.isLoading}
            undo={undo}
            redo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
          />
        </Grid>
      </Grid>

      {/* ── Floating popover for the selected issue ── */}
      <IssuePopover
        popperRef={popperRef}
        selectedAnchorEl={popoverAnchorElement}
        popoverOpen={isPopoverOpen}
        selectedIssue={selectedIssue}
        closePopover={closePopover}
        busy={isBusy}
        acceptIssue={acceptIssue}
        dismissIssue={dismissIssue}
        revertIssue={revertIssue}
        changeSelection={changeSelection}
        openIssues={openIssues}
        acceptedIssues={acceptedIssues}
      />

      {/* ── "Unaddressed suggestions" warning modal ── */}
      <UnaddressedModal
        open={isWarningModalOpen}
        onClose={handleStayOnPage}
        onAction={handleLeavePage}
        onContinue={handleStayOnPage}
        actionLabel={
          isPendingDownload ? 'Download Anyway' : pendingExternalUrl ? 'Open Link' : 'Leave'
        }
        title="Unaddressed Suggestions"
        message={`You still have ${openIssues.length} unaddressed suggestion${openIssues.length === 1 ? '' : 's'}. Changes will not be made on unaddressed suggestions. Do you wish to proceed?`}
      />
    </Box>
  );
});

export default StyleGuideChecker;
